import { c as _c } from "react/compiler-runtime";
import { type Dispatch, useCallback, useId, useState, type KeyboardEvent, type RefObject, type SetStateAction } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { GroupsRequestParams } from "@/entities/IGroup";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import type { Mutable, NonNullableNotRequired } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
const MAX_RESULTS = 7;
const DEBOUNCE_MS = 200;
type CreateWaveGroupSearchParams = {
  readonly defaultLabel: string;
  readonly disabled: boolean;
  readonly inputRef: RefObject<HTMLInputElement | null>;
  readonly selectedGroup: ApiGroupFull | null;
  readonly allowClear: boolean;
  readonly onSelect: (group: ApiGroupFull | null) => void;
  readonly wrapperRef: RefObject<HTMLDivElement | null>;
};
type CreateWaveGroupSearchState = {
  readonly selectedGroupKey: string | null;
  readonly inputValue: string;
  readonly searchCriteria: string;
  readonly activeIndex: number;
};
function getSelectedGroupKey(group: ApiGroupFull | null): string | null {
  if (!group) {
    return null;
  }
  return JSON.stringify([group.id, group.name]);
}
function createSearchState({
  selectedGroupKey,
  selectedGroupName
}: {
  readonly selectedGroupKey: string | null;
  readonly selectedGroupName: string;
}): CreateWaveGroupSearchState {
  return {
    selectedGroupKey,
    inputValue: selectedGroupName,
    searchCriteria: selectedGroupName,
    activeIndex: -1
  };
}
function getEffectiveSearchState({
  searchState,
  selectedGroupKey,
  selectedGroupName
}: {
  readonly searchState: CreateWaveGroupSearchState;
  readonly selectedGroupKey: string | null;
  readonly selectedGroupName: string;
}): CreateWaveGroupSearchState {
  if (searchState.selectedGroupKey === selectedGroupKey) {
    return searchState;
  }
  return createSearchState({
    selectedGroupKey,
    selectedGroupName
  });
}
function useCreateWaveGroupSuggestions(t0) {
  const $ = _c(15);
  const {
    debouncedValue,
    disabled,
    isOpen
  } = t0;
  const t1 = debouncedValue || null;
  let t2;
  if ($[0] !== t1) {
    t2 = [QueryKey.GROUPS, {
      group_name: t1
    }];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== debouncedValue) {
    t3 = async () => {
      const params = {};
      if (debouncedValue) {
        params.group_name = debouncedValue;
      }
      return await commonApiFetch({
        endpoint: "groups",
        params
      });
    };
    $[2] = debouncedValue;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isOpen && !disabled;
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      placeholderData: keepPreviousData
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data,
    isFetching
  } = useQuery(t5);
  let t6;
  if ($[8] !== data) {
    t6 = data ?? [];
    $[8] = data;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = t6.slice(0, MAX_RESULTS);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isFetching || $[13] !== t7) {
    t8 = {
      isFetching,
      suggestions: t7
    };
    $[12] = isFetching;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function useCreateWaveGroupKeyboardNavigation(t0) {
  const $ = _c(8);
  const {
    activeIndex,
    closeSearch,
    isOpen,
    onOptionSelect,
    setActiveIndex,
    setIsOpen,
    suggestions
  } = t0;
  let t1;
  if ($[0] !== activeIndex || $[1] !== closeSearch || $[2] !== isOpen || $[3] !== onOptionSelect || $[4] !== setActiveIndex || $[5] !== setIsOpen || $[6] !== suggestions) {
    t1 = event => {
      if (!isOpen) {
        if (event.key === "ArrowDown") {
          setIsOpen(true);
          setActiveIndex(0);
          event.preventDefault();
        }
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex(prev => {
          const nextIndex = prev + 1;
          if (nextIndex >= suggestions.length) {
            return suggestions.length ? 0 : -1;
          }
          return nextIndex;
        });
      } else {
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setActiveIndex(prev_0 => {
            if (suggestions.length === 0) {
              return -1;
            }
            if (prev_0 <= 0) {
              return suggestions.length - 1;
            }
            return prev_0 - 1;
          });
        } else {
          if (event.key === "Enter") {
            if (activeIndex >= 0 && activeIndex < suggestions.length) {
              event.preventDefault();
              onOptionSelect(suggestions[activeIndex]);
            }
          } else {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              closeSearch();
            }
          }
        }
      }
    };
    $[0] = activeIndex;
    $[1] = closeSearch;
    $[2] = isOpen;
    $[3] = onOptionSelect;
    $[4] = setActiveIndex;
    $[5] = setIsOpen;
    $[6] = suggestions;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
function useCreateWaveGroupSearchState(t0) {
  const $ = _c(22);
  const {
    selectedGroupKey,
    selectedGroupName
  } = t0;
  let t1;
  if ($[0] !== selectedGroupKey || $[1] !== selectedGroupName) {
    t1 = () => createSearchState({
      selectedGroupKey,
      selectedGroupName
    });
    $[0] = selectedGroupKey;
    $[1] = selectedGroupName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [searchState, setSearchState] = useState(t1);
  const [debouncedValue, setDebouncedValue] = useState(selectedGroupName);
  let t2;
  if ($[3] !== searchState || $[4] !== selectedGroupKey || $[5] !== selectedGroupName) {
    t2 = getEffectiveSearchState({
      searchState,
      selectedGroupKey,
      selectedGroupName
    });
    $[3] = searchState;
    $[4] = selectedGroupKey;
    $[5] = selectedGroupName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const effectiveSearchState = t2;
  const {
    activeIndex,
    inputValue,
    searchCriteria
  } = effectiveSearchState;
  let t3;
  if ($[7] !== selectedGroupKey || $[8] !== selectedGroupName) {
    t3 = getNextState => {
      setSearchState(current => {
        const effectiveCurrent = getEffectiveSearchState({
          searchState: current,
          selectedGroupKey,
          selectedGroupName
        });
        return getNextState(effectiveCurrent);
      });
    };
    $[7] = selectedGroupKey;
    $[8] = selectedGroupName;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const updateSearchState = t3;
  let t4;
  if ($[10] !== updateSearchState) {
    t4 = nextActiveIndex => {
      updateSearchState(current_0 => ({
        ...current_0,
        activeIndex: typeof nextActiveIndex === "function" ? nextActiveIndex(current_0.activeIndex) : nextActiveIndex
      }));
    };
    $[10] = updateSearchState;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const setActiveIndex = t4;
  let t5;
  if ($[12] !== searchCriteria) {
    t5 = () => {
      setDebouncedValue(searchCriteria.trim());
    };
    $[12] = searchCriteria;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== searchCriteria) {
    t6 = [searchCriteria];
    $[14] = searchCriteria;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  useDebounce(t5, DEBOUNCE_MS, t6);
  let t7;
  if ($[16] !== activeIndex || $[17] !== debouncedValue || $[18] !== inputValue || $[19] !== searchCriteria || $[20] !== setActiveIndex) {
    t7 = {
      activeIndex,
      debouncedValue,
      inputValue,
      searchCriteria,
      setActiveIndex,
      setSearchState
    };
    $[16] = activeIndex;
    $[17] = debouncedValue;
    $[18] = inputValue;
    $[19] = searchCriteria;
    $[20] = setActiveIndex;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}
export function useCreateWaveGroupSearch(t0) {
  const $ = _c(68);
  const {
    defaultLabel,
    disabled,
    inputRef,
    selectedGroup,
    allowClear,
    onSelect,
    wrapperRef
  } = t0;
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  let t1;
  if ($[0] !== selectedGroup) {
    t1 = getSelectedGroupKey(selectedGroup);
    $[0] = selectedGroup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedGroupKey = t1;
  const selectedGroupName = selectedGroup?.name ?? "";
  const [isOpen, setIsOpen] = useState(false);
  let t2;
  if ($[2] !== selectedGroupKey || $[3] !== selectedGroupName) {
    t2 = {
      selectedGroupKey,
      selectedGroupName
    };
    $[2] = selectedGroupKey;
    $[3] = selectedGroupName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    activeIndex,
    debouncedValue,
    inputValue,
    searchCriteria,
    setActiveIndex,
    setSearchState
  } = useCreateWaveGroupSearchState(t2);
  let t3;
  if ($[5] !== debouncedValue || $[6] !== disabled || $[7] !== isOpen) {
    t3 = {
      debouncedValue,
      disabled,
      isOpen
    };
    $[5] = debouncedValue;
    $[6] = disabled;
    $[7] = isOpen;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const {
    isFetching,
    suggestions
  } = useCreateWaveGroupSuggestions(t3);
  let t4;
  if ($[9] !== setActiveIndex) {
    t4 = () => {
      setIsOpen(false);
      setActiveIndex(-1);
    };
    $[9] = setActiveIndex;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const closeSearch = t4;
  let t5;
  if ($[11] !== selectedGroupKey || $[12] !== selectedGroupName || $[13] !== setSearchState) {
    t5 = () => {
      setIsOpen(false);
      setSearchState(createSearchState({
        selectedGroupKey,
        selectedGroupName
      }));
    };
    $[11] = selectedGroupKey;
    $[12] = selectedGroupName;
    $[13] = setSearchState;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const dismissSearch = t5;
  let t6;
  if ($[15] !== allowClear || $[16] !== disabled || $[17] !== inputRef || $[18] !== onSelect || $[19] !== setSearchState) {
    t6 = () => {
      if (!allowClear || disabled) {
        return;
      }
      setSearchState({
        selectedGroupKey: null,
        inputValue: "",
        searchCriteria: "",
        activeIndex: -1
      });
      onSelect(null);
      setIsOpen(true);
      inputRef.current?.focus();
    };
    $[15] = allowClear;
    $[16] = disabled;
    $[17] = inputRef;
    $[18] = onSelect;
    $[19] = setSearchState;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const clearSelection = t6;
  let t7;
  if ($[21] !== dismissSearch || $[22] !== isOpen) {
    t7 = () => {
      if (!isOpen) {
        return;
      }
      dismissSearch();
    };
    $[21] = dismissSearch;
    $[22] = isOpen;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  useClickAway(wrapperRef, t7);
  let t8;
  if ($[24] !== dismissSearch || $[25] !== isOpen) {
    t8 = () => {
      if (!isOpen) {
        return;
      }
      dismissSearch();
    };
    $[24] = dismissSearch;
    $[25] = isOpen;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  useKeyPressEvent("Escape", t8);
  let t9;
  if ($[27] !== disabled || $[28] !== setActiveIndex) {
    t9 = () => {
      if (disabled) {
        return;
      }
      setIsOpen(true);
      setActiveIndex(-1);
    };
    $[27] = disabled;
    $[28] = setActiveIndex;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  const onInputFocus = t9;
  let t10;
  if ($[30] !== selectedGroupKey || $[31] !== setSearchState) {
    t10 = value => {
      setSearchState({
        selectedGroupKey,
        inputValue: value,
        searchCriteria: value,
        activeIndex: -1
      });
      setIsOpen(true);
    };
    $[30] = selectedGroupKey;
    $[31] = setSearchState;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const handleInputChange = t10;
  let t11;
  if ($[33] !== closeSearch || $[34] !== inputRef || $[35] !== onSelect || $[36] !== selectedGroupKey || $[37] !== setSearchState) {
    t11 = group => {
      setSearchState({
        selectedGroupKey,
        inputValue: group.name,
        searchCriteria: group.name,
        activeIndex: -1
      });
      onSelect(group);
      closeSearch();
      inputRef.current?.focus();
    };
    $[33] = closeSearch;
    $[34] = inputRef;
    $[35] = onSelect;
    $[36] = selectedGroupKey;
    $[37] = setSearchState;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const onOptionSelect = t11;
  let t12;
  if ($[39] !== activeIndex || $[40] !== dismissSearch || $[41] !== isOpen || $[42] !== onOptionSelect || $[43] !== setActiveIndex || $[44] !== suggestions) {
    t12 = {
      activeIndex,
      closeSearch: dismissSearch,
      isOpen,
      onOptionSelect,
      setActiveIndex,
      setIsOpen,
      suggestions
    };
    $[39] = activeIndex;
    $[40] = dismissSearch;
    $[41] = isOpen;
    $[42] = onOptionSelect;
    $[43] = setActiveIndex;
    $[44] = suggestions;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  const handleKeyDown = useCreateWaveGroupKeyboardNavigation(t12);
  let t13;
  if ($[46] !== inputValue) {
    t13 = inputValue.trim();
    $[46] = inputValue;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const hasValue = t13.length > 0;
  const helperText = `Current group: ${selectedGroup?.name ?? defaultLabel}`;
  const showClearButton = allowClear && (hasValue || !!selectedGroup) && !disabled;
  const showNoResults = !isFetching && isOpen && suggestions.length === 0;
  const activeOptionId = activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined;
  let t14;
  if ($[48] !== activeIndex || $[49] !== activeOptionId || $[50] !== clearSelection || $[51] !== handleInputChange || $[52] !== handleKeyDown || $[53] !== hasValue || $[54] !== helperText || $[55] !== inputId || $[56] !== inputValue || $[57] !== isFetching || $[58] !== isOpen || $[59] !== listboxId || $[60] !== onInputFocus || $[61] !== onOptionSelect || $[62] !== searchCriteria || $[63] !== setActiveIndex || $[64] !== showClearButton || $[65] !== showNoResults || $[66] !== suggestions) {
    t14 = {
      activeIndex,
      activeOptionId,
      clearSelection,
      handleInputChange,
      handleKeyDown,
      hasValue,
      helperText,
      inputId,
      inputValue,
      isFetching,
      isOpen,
      listboxId,
      onInputFocus,
      onOptionSelect,
      searchCriteria,
      setActiveIndex,
      showClearButton,
      showNoResults,
      suggestions
    };
    $[48] = activeIndex;
    $[49] = activeOptionId;
    $[50] = clearSelection;
    $[51] = handleInputChange;
    $[52] = handleKeyDown;
    $[53] = hasValue;
    $[54] = helperText;
    $[55] = inputId;
    $[56] = inputValue;
    $[57] = isFetching;
    $[58] = isOpen;
    $[59] = listboxId;
    $[60] = onInputFocus;
    $[61] = onOptionSelect;
    $[62] = searchCriteria;
    $[63] = setActiveIndex;
    $[64] = showClearButton;
    $[65] = showNoResults;
    $[66] = suggestions;
    $[67] = t14;
  } else {
    t14 = $[67];
  }
  return t14;
}